// Pure command-palette index: build a flat, searchable list of navigation
// targets (screens + records) from data already in memory. No React, no DOM,
// no Firestore — tested directly like hashRoute.js. See
// docs/superpowers/specs/2026-07-15-command-palette-v1-design.md.
import { fmtMoney, objToken, engagementType, engagementTypeLabel } from './format.js'
import { SCREENS } from './routes.js'

// The fixed set of top-level destinations comes from the route registry so the
// palette, sidebar, and hash layer can never drift. Labels MUST match the sidebar.
export { SCREENS }

/**
 * Subsequence fuzzy match. Returns null if every char of `query` does not
 * appear in `text` in order (case-insensitive). Otherwise a score: each matched
 * char is worth a base amount, with bonuses for landing on a word start and for
 * continuing a contiguous run, and a small penalty for later positions. Higher
 * is better. Empty query -> 0.
 */
export function fuzzyScore(query, text) {
  const q = String(query).toLowerCase()
  const t = String(text).toLowerCase()
  if (!q) return 0
  let score = 0, ti = 0, prev = -2
  for (let qi = 0; qi < q.length; qi++) {
    const c = q[qi]
    let found = -1
    for (let k = ti; k < t.length; k++) { if (t[k] === c) { found = k; break } }
    if (found === -1) return null
    let charScore = 10
    if (found === prev + 1) charScore += 15                        // contiguous run
    if (found === 0 || ' @.-/'.includes(t[found - 1])) charScore += 10 // word start
    charScore -= found * 0.1                                        // earlier is better
    score += charScore
    prev = found
    ti = found + 1
  }
  return score
}

function contactName(c) {
  const full = `${c.firstName || ''} ${c.lastName || ''}`.trim()
  return full || c.email || 'Unnamed contact'
}

/**
 * Build the flat item list. Each item: { id, group, title, subtitle, keywords, route }.
 * `keywords` is lowercased extra match text (not displayed). All list args default
 * to [] so partial data (e.g. engagements scoped to one type) is safe.
 */
export function buildIndex({ contacts = [], companies = [], engagements = [], projects = [] } = {}) {
  const companyName = {}
  companies.forEach((co) => { companyName[co.id] = co.name || '' })

  const screens = SCREENS.map((s) => ({
    id: `screen:${s.key}`, group: 'screen', title: s.title, subtitle: '',
    keywords: s.keywords, route: s.route,
  }))

  const projectItems = projects.map((p) => ({
    id: objToken('project', p.id), group: 'project',
    title: p.name || 'Untitled project', subtitle: '', keywords: '',
    route: { type: 'project', projectId: p.id },
  }))

  const contactItems = contacts.map((c) => {
    const co = companyName[c.primaryCompanyId] || ''
    return {
      id: objToken('contact', c.id), group: 'contact',
      title: contactName(c), subtitle: co || c.email || '',
      keywords: `${c.email || ''} ${co}`.toLowerCase().trim(),
      route: { type: 'crm', object: 'contacts', recordId: c.id },
    }
  })

  const companyItems = companies.map((co) => ({
    id: objToken('company', co.id), group: 'company',
    title: co.name || 'Unnamed company', subtitle: co.domain || '',
    keywords: (co.domain || '').toLowerCase(),
    route: { type: 'crm', object: 'companies', recordId: co.id },
  }))

  const engagementItems = engagements.map((e) => ({
    id: objToken('engagement', e.id), group: 'engagement',
    title: e.title || 'Untitled engagement',
    subtitle: [e.org, fmtMoney(e.fee)].filter(Boolean).join(' · '),
    keywords: `${e.org || ''} ${engagementTypeLabel(engagementType(e))}`.toLowerCase().trim(),
    route: { type: 'engagements', board: engagementType(e), recordId: e.id },
  }))

  return [...screens, ...projectItems, ...contactItems, ...companyItems, ...engagementItems]
}

// Fixed display order + human labels for result groups.
const GROUP_ORDER = ['screen', 'project', 'contact', 'company', 'engagement']
const GROUP_LABELS = {
  screen: 'Go to', project: 'Projects', contact: 'Contacts',
  company: 'Companies', engagement: 'Engagements',
}

/**
 * Group + rank the index for a query. Empty/blank query -> screens group only
 * (launcher mode). Otherwise fuzzy-rank each item on `title + ' ' + keywords`,
 * drop non-matches, sort per group by score desc (tie: title asc), cap each
 * group to perGroupLimit, and return non-empty groups in fixed display order.
 */
export function search(index, query, { perGroupLimit = 6 } = {}) {
  const q = String(query || '').trim()
  const buckets = {}
  GROUP_ORDER.forEach((g) => { buckets[g] = [] })

  if (!q) {
    index.forEach((item) => { if (item.group === 'screen') buckets.screen.push({ item, score: 0 }) })
  } else {
    index.forEach((item) => {
      const score = fuzzyScore(q, `${item.title} ${item.keywords}`)
      if (score !== null) buckets[item.group].push({ item, score })
    })
  }

  return GROUP_ORDER
    .map((group) => {
      const scored = buckets[group]
      if (!scored.length) return null
      if (q) scored.sort((a, b) => (b.score - a.score) || a.item.title.localeCompare(b.item.title))
      const capped = q ? scored.slice(0, perGroupLimit) : scored
      return { group, label: GROUP_LABELS[group], items: capped.map((s) => s.item) }
    })
    .filter(Boolean)
}

/** Wrap-around index move for keyboard nav. Returns 0 when count is 0. */
export function moveActive(count, current, delta) {
  if (!count) return 0
  return ((current + delta) % count + count) % count
}
